import { createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash-es"
import valueMatches from "utils/valueMatches"

const SLICE_NAME = "facilities"

const INITIAL_STATE = {
  departmentIdMap: {},
  departments: [],
  facilities: [],
  facilityIdMap: {},
}

const buildDepartmentIdMap = (facilities = []) => cloneDeep(facilities).reduce((map, facility) => {
  const { departments = [] } = facility

  departments.forEach((department) => {
    map[department.id] = department
  })

  return map
}, {})

// Ignoring .listLastOnForms
const buildDepartmentList = (facilities = []) => (
  cloneDeep(facilities).flatMap((facility) => facility.departments).sort((a, b) => {
    // Sort active departments before inactive departments
    if (a.active !== b.active) {
      return a.active ? -1 : 1
    }

    // Sort alphabetically by name
    return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1
  })
)

const buildFacilityIdMap = (facilities = []) => cloneDeep(facilities).reduce((map, facility) => {
  map[facility.id] = facility
  return map
}, {})

export const facilitiesSlice = createSlice({
  name: "facilities",
  initialState: INITIAL_STATE,
  reducers: {
    setFacilities: (state, action) => {
      const { payload: newFacilities } = action

      state.departmentIdMap = buildDepartmentIdMap(newFacilities)
      state.departments = buildDepartmentList(newFacilities)
      state.facilities = newFacilities
      state.facilityIdMap = buildFacilityIdMap(newFacilities)
    },
  },
})

export const {
  setFacilities,
} = facilitiesSlice.actions

export const getDepartments = (state) => state[SLICE_NAME].departments

export const activeDepartments = (state) => state[SLICE_NAME].departments.filter((department) => department.active === true)

export const getActiveOrMatchingDepartments = (ids) => (state) => (
  state[SLICE_NAME].departments.filter((department) => department.active || valueMatches(department.id, ids))
)

export const activeDepartmentsWithFacilityNames = (state) => activeDepartments(state).map((department) => ({
  ...department,
  facilityName: state[SLICE_NAME].facilityIdMap[department.facilityId]?.name,
})).sort((a, b) => {
  // Sort by facility name first, then department name, to match the "{facilityName}: {name}" label
  const facilityNameA = (a.facilityName ?? "").toLowerCase()
  const facilityNameB = (b.facilityName ?? "").toLowerCase()

  if (facilityNameA !== facilityNameB) {
    return facilityNameA < facilityNameB ? -1 : 1
  }

  return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1
})

export const getDepartmentFacilityMapping = (state) => state[SLICE_NAME].departments.reduce((map, department) => {
  map[department.id] = department.facilityId

  return map
}, {})

export const getFacilities = (state) => state[SLICE_NAME].facilities

export const getActiveOrMatchingFacilities = (ids) => (state) => (
  state[SLICE_NAME].facilities.filter((facility) => facility.active || valueMatches(facility.id, ids))
)

export const getFacilityDepartments = (state) => state[SLICE_NAME].departments.reduce((map, department) => {
  if (map[department.facilityId]) {
    map[department.facilityId].push(department)
  } else {
    map[department.facilityId] = [department]
  }

  return map
}, {})

export const getFacilityIdMap = (state) => state[SLICE_NAME].facilityIdMap

export const getFacilityAndDepartmentFromDepartmentId = (departmentId) => (state) => {
  if (departmentId === null || departmentId === undefined) return []

  const department = state[SLICE_NAME].departmentIdMap[departmentId]
  if (!department) return []

  const facility = state[SLICE_NAME].facilityIdMap[department.facilityId]
  if (!facility) return []

  return [facility, department]
}

export default facilitiesSlice.reducer
